import { z } from "zod";
import { zId } from "./common.js";

/**
 * CT-RM-29: NCOA (National Change of Address) and CASS address hygiene.
 *
 * Staff run an NCOA pass over a segment (or every constituent): export a
 * vendor-format CSV of primary addresses, send it to their NCOA vendor, and
 * import the vendor's results CSV. Results land in a review queue; accepting a
 * move replaces the primary address (the old one is kept as inactive history),
 * accepting a no-forwarding / undeliverable result flags the address so mail
 * runs exclude it by default. A built-in test provider simulates the vendor
 * round trip (no real vendor is called).
 */

/** USPS requires an NCOA pass within this many days for presort mail discounts. */
export const NCOA_STALE_DAYS = 95;

export const NCOA_RESULT_KINDS = ["move", "no_forwarding", "undeliverable", "standardized", "no_match"] as const;
export const zNcoaResultKind = z.enum(NCOA_RESULT_KINDS);
export type NcoaResultKind = z.infer<typeof zNcoaResultKind>;

export const NCOA_RESULT_KIND_LABELS: Record<NcoaResultKind, string> = {
  move: "Moved — new address",
  no_forwarding: "Moved, left no forwarding",
  undeliverable: "Undeliverable",
  standardized: "Address standardized (CASS)",
  no_match: "No change",
};

/** Standard NCOALink return / action codes and what each means for the address. */
export const NCOA_RETURN_CODES: { code: string; label: string; kind: NcoaResultKind }[] = [
  { code: "A", label: "COA match — new address provided", kind: "move" },
  { code: "91", label: "COA match — secondary number dropped from COA", kind: "move" },
  { code: "92", label: "COA match — secondary number dropped from input", kind: "move" },
  { code: "00", label: "No match", kind: "no_match" },
  { code: "01", label: "Found COA — foreign move", kind: "undeliverable" },
  { code: "02", label: "Found COA — moved, left no forwarding address", kind: "no_forwarding" },
  { code: "03", label: "Found COA — PO box closed, no order", kind: "no_forwarding" },
  { code: "04", label: "Cannot match — street address with secondary", kind: "no_match" },
  { code: "05", label: "Found COA — new 11-digit DPBC is ambiguous", kind: "no_match" },
  { code: "06", label: "Cannot match — conflicting instructions", kind: "no_match" },
  { code: "07", label: "Cannot match — conflicting instructions", kind: "no_match" },
  { code: "08", label: "Cannot match — high-rise default", kind: "no_match" },
  { code: "09", label: "Cannot match — rural route default", kind: "no_match" },
  { code: "10", label: "Cannot match — insufficient COA name data", kind: "no_match" },
  { code: "11", label: "Cannot match — insufficient match data", kind: "no_match" },
  { code: "12", label: "Cannot match — hyphenated name", kind: "no_match" },
  { code: "13", label: "Cannot match — business name", kind: "no_match" },
  { code: "14", label: "Found COA — new address would not convert", kind: "undeliverable" },
  { code: "15", label: "Cannot match — compound name", kind: "no_match" },
  { code: "16", label: "Cannot match — gender", kind: "no_match" },
  { code: "17", label: "Cannot match — insufficient COA address data", kind: "no_match" },
  { code: "18", label: "Cannot match — last name", kind: "no_match" },
  { code: "19", label: "Found COA — new address not deliverable", kind: "undeliverable" },
  { code: "20", label: "Found COA — multiple responses", kind: "no_match" },
];

export const NCOA_MOVE_TYPES = ["individual", "family", "business"] as const;
export const zNcoaMoveType = z.enum(NCOA_MOVE_TYPES);
export type NcoaMoveType = z.infer<typeof zNcoaMoveType>;

/** NCOA vendors report the move type as I / F / B */
export function parseMoveType(raw: string | null | undefined): NcoaMoveType | null {
  const v = (raw ?? "").trim().toLowerCase();
  if (!v) return null;
  if (v === "i" || v.startsWith("ind")) return "individual";
  if (v === "f" || v.startsWith("fam")) return "family";
  if (v === "b" || v.startsWith("bus")) return "business";
  return null;
}

/** footnote text that implies a result kind even when the return code is blank or unknown */
export function kindFromFootnotes(footnotes: string[]): NcoaResultKind | null {
  const text = footnotes.join(" ").toLowerCase();
  if (/no\s*forward|left no (forwarding )?address|\bmlna\b|box closed|\bbcno\b/.test(text)) return "no_forwarding";
  if (/undeliverable|vacant|no such (street|number|address)|foreign move|not deliverable|return to sender/.test(text)) return "undeliverable";
  return null;
}

/**
 * Classifies one vendor row. Unknown codes fall back to the footnotes, then to
 * whether a new address was supplied.
 */
export function classifyNcoaResult(returnCode: string, footnotes: string[], hasNewAddress: boolean): NcoaResultKind {
  const code = returnCode.trim().toUpperCase().replace(/^0(?=[A-Z])/, "");
  const known = NCOA_RETURN_CODES.find((c) => c.code === code || (code.length === 1 && /\d/.test(code) && c.code === `0${code}`));
  const byFootnote = kindFromFootnotes(footnotes);
  if (known) {
    // a "no match" code with an explicit no-forwarding footnote is still a no-forwarding
    if (known.kind === "no_match" && byFootnote) return byFootnote;
    if (known.kind === "move" && !hasNewAddress) return byFootnote ?? "no_match";
    return known.kind;
  }
  if (byFootnote) return byFootnote;
  return hasNewAddress ? "move" : "no_match";
}

/** Fields of a vendor results file CauseTide understands; recordId is the only required one. */
export const NCOA_RESULT_FIELDS = [
  "recordId",
  "returnCode",
  "moveType",
  "moveDate",
  "newLine1",
  "newLine2",
  "newCity",
  "newState",
  "newPostalCode",
  "newCountry",
  "footnotes",
] as const;
export type NcoaResultField = (typeof NCOA_RESULT_FIELDS)[number];

export const NCOA_RESULT_FIELD_LABELS: Record<NcoaResultField, string> = {
  recordId: "Record ID",
  returnCode: "NCOA return code",
  moveType: "Move type (I/F/B)",
  moveDate: "Move effective date",
  newLine1: "New address 1",
  newLine2: "New address 2",
  newCity: "New city",
  newState: "New state",
  newPostalCode: "New ZIP / postal code",
  newCountry: "New country",
  footnotes: "Footnotes",
};

/** CauseTide field -> vendor CSV column header */
export const zNcoaColumnMapping = z
  .object({
    recordId: z.string().min(1),
    returnCode: z.string().optional(),
    moveType: z.string().optional(),
    moveDate: z.string().optional(),
    newLine1: z.string().optional(),
    newLine2: z.string().optional(),
    newCity: z.string().optional(),
    newState: z.string().optional(),
    newPostalCode: z.string().optional(),
    newCountry: z.string().optional(),
    footnotes: z.string().optional(),
  })
  .strict();
export type NcoaColumnMapping = z.infer<typeof zNcoaColumnMapping>;

const HEADER_ALIASES: Record<NcoaResultField, string[]> = {
  recordId: ["record_id", "recordid", "id", "record", "customer_id", "key", "address_id"],
  returnCode: ["ncoa_return_code", "return_code", "ncoa_code", "ncoalink_return_code", "action_code", "ncoa_action_code", "rc", "code"],
  moveType: ["move_type", "coa_move_type", "ncoa_move_type", "movetype", "move_type_code"],
  moveDate: ["move_date", "move_effective_date", "coa_effective_date", "effective_date", "movedate", "move_eff_date"],
  newLine1: ["new_address_1", "new_address1", "new_addr1", "new_line1", "new_address", "new_delivery_address", "new_primary_address", "address_1_new"],
  newLine2: ["new_address_2", "new_address2", "new_addr2", "new_line2", "new_secondary_address", "address_2_new"],
  newCity: ["new_city", "city_new"],
  newState: ["new_state", "state_new"],
  newPostalCode: ["new_zip", "new_zip_code", "new_zipcode", "new_postal_code", "new_zip5", "zip_new"],
  newCountry: ["new_country", "country_new"],
  footnotes: ["footnotes", "footnote", "ncoa_footnotes", "notes", "comments", "return_code_description", "description"],
};

const normHeader = (h: string) => h.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

/** best-guess mapping from a vendor file's headers (exact alias match, normalized) */
export function suggestNcoaMapping(headers: string[]): Partial<NcoaColumnMapping> {
  const out: Partial<NcoaColumnMapping> = {};
  const used = new Set<string>();
  for (const field of NCOA_RESULT_FIELDS) {
    const hit = headers.find((h) => !used.has(h) && HEADER_ALIASES[field].includes(normHeader(h)));
    if (hit) {
      out[field] = hit;
      used.add(hit);
    }
  }
  return out;
}

/** columns of the export CauseTide hands to an NCOA vendor */
export const NCOA_EXPORT_COLUMNS = ["RECORD_ID", "CONSTITUENT_ID", "FULL_NAME", "FIRST_NAME", "LAST_NAME", "ADDRESS_1", "ADDRESS_2", "CITY", "STATE", "ZIP", "COUNTRY"] as const;

export const zCreateNcoaRun = z.object({
  name: z.string().trim().min(1).max(200),
  /** null / omitted = every constituent with a primary address */
  segmentId: zId.nullable().optional(),
});
export type CreateNcoaRun = z.infer<typeof zCreateNcoaRun>;

export const NCOA_RUN_STATUSES = ["exported", "results_imported"] as const;
export type NcoaRunStatus = (typeof NCOA_RUN_STATUSES)[number];

export interface NcoaRunRow {
  id: string;
  name: string;
  segmentId: string | null;
  segmentName: string | null;
  status: NcoaRunStatus;
  provider: string | null;
  recordCount: number;
  resultCount: number;
  pendingCount: number;
  createdAt: string;
  importedAt: string | null;
}

export interface NcoaImportPreview {
  headers: string[];
  rowCount: number;
  preview: Record<string, string>[];
  suggestedMapping: Partial<NcoaColumnMapping>;
}

export interface NcoaImportSummary {
  runId: string;
  rows: number;
  imported: number;
  /** record ids that didn't match an address in this organization */
  skipped: number;
  byKind: Record<NcoaResultKind, number>;
  queued: number;
}

export const NCOA_RESULT_STATUSES = ["pending", "accepted", "rejected", "no_change"] as const;
export const zNcoaResultStatus = z.enum(NCOA_RESULT_STATUSES);
export type NcoaResultStatus = z.infer<typeof zNcoaResultStatus>;

export interface NcoaAddressLines {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface NcoaResultRow {
  id: string;
  runId: string;
  runName: string;
  constituentId: string;
  constituentName: string;
  returnCode: string;
  returnCodeLabel: string | null;
  kind: NcoaResultKind;
  moveType: NcoaMoveType | null;
  moveDate: string | null;
  footnotes: string[];
  original: NcoaAddressLines;
  proposed: NcoaAddressLines | null;
  status: NcoaResultStatus;
  reviewedAt: string | null;
  createdAt: string;
}

export const zNcoaResultQuery = z.object({
  status: zNcoaResultStatus.optional(),
  kind: zNcoaResultKind.optional(),
  returnCode: z.string().trim().max(10).optional(),
  runId: zId.optional(),
});
export type NcoaResultQuery = z.infer<typeof zNcoaResultQuery>;

export const zNcoaBulkAccept = z.object({
  returnCode: z.string().trim().min(1).max(10),
  runId: zId.optional(),
});
export type NcoaBulkAccept = z.infer<typeof zNcoaBulkAccept>;

export interface NcoaStatus {
  /** most recent results import across the org */
  lastNcoaAt: string | null;
  staleDays: number;
  /** true when no pass has run, or the last one is older than NCOA_STALE_DAYS */
  stale: boolean;
  primaryAddressCount: number;
  /** primary addresses never checked or checked more than NCOA_STALE_DAYS ago */
  staleAddressCount: number;
  undeliverableCount: number;
  pendingReviewCount: number;
}

export interface AddressHistoryEntry extends NcoaAddressLines {
  id: string;
  type: string;
  isActive: false;
  source: "ncoa" | "cass";
  reason: string;
  ncoaResultId: string | null;
  inactivatedAt: string;
}

export interface ConstituentAddressHygiene {
  addresses: {
    id: string;
    isPrimary: boolean;
    ncoaCheckedAt: string | null;
    undeliverable: boolean;
    undeliverableReason: string | null;
  }[];
  history: AddressHistoryEntry[];
}
